import unitStatProgression from './gen_unit_stat_progression.json';
import relicTierDefs from './gen_relic_tier_defs.json';

export interface RawStatAmount
{
    stat: number;
    amount: number;
}

export interface ProjectedUnitStats
{
    baseId: string;
    baseStats: RawStatAmount[];
}

function isValidChar(baseId: string): baseId is keyof typeof unitStatProgression
{
    return baseId in unitStatProgression;
}

function isValidRelic(defName: string): defName is keyof typeof relicTierDefs
{
    return defName in relicTierDefs;
}

interface IStatContainer
{
    [index: string]: number;
}

// Whether we have progression data to project this unit's stats at an assumed
// star/relic level (the "solve as if" feature); units without it cannot be projected.
export function canProjectUnit(baseId: string): boolean
{
    return isValidChar(baseId);
}

// Highest displayed relic level (R0 = 0) the game currently defines for this unit,
// or 0 when the unit cannot be projected. Used to bound the picker.
export function getMaxRelicLevel(baseId: string): number
{
    if (!isValidChar(baseId))
        return 0;
    return unitStatProgression[baseId].relicTierDefs.length;
}

export function computeUnitStats(baseId: string, starLevel: number, relicLevel: number): ProjectedUnitStats
{
    let ret: ProjectedUnitStats = {
        baseId,
        baseStats: []
    };

    if (!isValidChar(baseId) || starLevel < 1 || starLevel > 7)
        return ret;

    let stats: IStatContainer = Object.assign({}, unitStatProgression[baseId].gearStats);
    let growthModifiers: IStatContainer = Object.assign({"2": 0, "3": 0, "4": 0}, (unitStatProgression[baseId].statsByRarity as any)[starLevel.toString()]);

    if (relicLevel > 0 && relicLevel <= unitStatProgression[baseId].relicTierDefs.length)
    {
        let defName = unitStatProgression[baseId].relicTierDefs[relicLevel - 1];
        if (isValidRelic(defName))
        {
            let def = relicTierDefs[defName];
            for (let [statId, amt] of Object.entries(def.stats))
            {
                stats[statId] = (stats[statId] || 0) + amt;
            }

            for (let [statId, amt] of Object.entries(def.relicStatTable))
            {
                growthModifiers[statId] = (growthModifiers[statId] || 0) + amt;
            }
        }
    }

    // now update the stats based on the growth modifiers
    stats["2"] += growthModifiers["2"] * 85;
    stats["3"] += growthModifiers["3"] * 85;
    stats["4"] += growthModifiers["4"] * 85;
    if ("61" in stats)
    {
        for (let [statId, amt] of Object.entries(unitStatProgression[baseId].mastery))
        {
            stats[statId] = (stats[statId] || 0) + stats["61"] * amt;
        }
    }
    stats["1"] = (stats["1"] || 0) + stats["2"] * 18;
    stats["6"] = (stats["6"] || 0) + stats[unitStatProgression[baseId].primaryStat.toString()] * 1.4;
    stats["7"] = (stats["7"] || 0) + stats["4"] * 2.4;
    stats["8"] = (stats["8"] || 0) + stats["2"] * 0.14 + stats["3"] * 0.07;
    stats["9"] = (stats["9"] || 0) + stats["4"] * 0.1;
    stats["14"] = (stats["14"] || 0) + stats["3"] * 0.4;
    // hard coded mins for missing stats
    stats["12"] = (stats["12"] || 0) + 2.4;
    stats["13"] = (stats["13"] || 0) + 2.4;
    stats["15"] = (stats["15"] || 0);
    stats["16"] = (stats["16"] || 0) + 1.5;
    stats["18"] = (stats["18"] || 0) + 0.15;
    
    for (let [statId, amt] of Object.entries(stats))
    {
        ret.baseStats.push({stat: parseInt(statId), amount: amt});
    }

    return ret;
}
